import { UsenetSettings } from '../components/UsenetSettings';
import { useEffect, useState } from 'react';
import { api, ApiError, iptv, seerrApi, type IptvAccount, type Profile } from '../api';
import { useApp } from '../context';
import { LocalAccountSettings } from '../components/LocalAccount';
import { linkProps, navigate } from '../router';
import { ChannelManager } from '../components/ChannelManager';
import { ThemePicker } from '../components/ThemePicker';
import { Connections } from '../components/Connections';
import { PlaybackSettings } from '../components/PlaybackSettings';
import { ParentalSettings } from '../components/ParentalSettings';
import { InstallApp } from '../components/InstallApp';
import { BackupSettings } from '../components/BackupSettings';
import { openRemotePairing } from '../components/RemoteScreen';
import { NavSettings } from '../components/NavSettings';
import { LastfmSettings } from '../components/LastfmSettings';
import { TautulliSettings } from '../components/TautulliSettings';
import { SportCenterSettings } from '../components/SportCenterSettings';
import { AddonSettings, SmartSourceSettings, SourceOrderSetting } from '../components/AddonSettings';
import { usePolicy } from '../components/TopNav';
import { InviteSettings } from '../components/InviteSettings';
import { SyncSettings } from '../components/SyncSettings';
import { AdminOverview } from '../components/AdminOverview';
import { UsersAdmin } from './UsersAdmin';
import { PosterLabelSettings } from '../components/PosterLabels';
import { BadgeStudio } from '../components/BadgeStudio';
import { AvatarSettings } from '../components/AvatarSettings';
import { DebridSettings } from '../components/DebridSettings';
import { MyTaste } from '../components/TasteProfile';
import { useIsOwner } from '../owner';
import { QuickSetup } from '../components/QuickSetup';

type Group = 'start' | 'beheer' | 'server' | 'kijken' | 'uiterlijk' | 'bronnen' | 'account';

const GROUPS: { id: Group; label: string; owner?: boolean }[] = [
  { id: 'start', label: 'Start' },
  { id: 'beheer', label: 'Beheer', owner: true },
  { id: 'kijken', label: 'Kijken' },
  { id: 'uiterlijk', label: 'Uiterlijk' },
  { id: 'bronnen', label: 'Bronnen' },
  { id: 'account', label: 'Account' },
  { id: 'server', label: 'Server', owner: true },
];

/**
 * Onderdelen van Instellingen, in groepen (zoals Ether): de beheerder ziet ook Beheer en Server.
 * `href` = een eigen pagina buiten Instellingen; `words` = extra zoekwoorden.
 */
export const SETTINGS_SECTIONS = [
  { id: 'quick', group: 'start', label: 'Snel instellen', hint: 'In vier stappen klaar', words: 'snel instellen begin wizard start apparaat look accounts' },
  { id: 'overview', group: 'beheer', label: 'Overzicht', hint: 'Wie kijkt er, gezondheid, server', owner: true, words: 'status dashboard cpu geheugen netwerk activiteit gezondheid' },
  { id: 'users', group: 'beheer', label: 'Gebruikers en rechten', hint: 'Per profiel wat mag', owner: true, words: 'profielen rechten vrienden leeftijd kwaliteit' },
  { id: 'invites', group: 'beheer', label: 'Uitnodigingen', hint: 'Vrienden uitnodigen', owner: true, words: 'uitnodiging link wachtwoord resetten leden' },
  { id: 'activity', group: 'beheer', label: 'Activiteit (Tautulli)', hint: 'Geschiedenis en populair', owner: true, href: '/server', words: 'tautulli geschiedenis populair streams' },
  { id: 'av', group: 'kijken', label: 'Audio en ondertitels', hint: 'Taal en ondertitelstijl', words: 'taal ondertitels audio stijl' },
  { id: 'playback', group: 'kijken', label: 'Afspelen en bronnen', hint: 'Kwaliteit, bronvolgorde', words: 'afspelen kwaliteit bron volgorde autoplay' },
  { id: 'iptv', group: 'kijken', label: 'Live TV', hint: 'IPTV-accounts en zenders', words: 'iptv xtream zenders epg gids favorieten' },
  { id: 'sport', group: 'kijken', label: 'Sport', hint: 'Clubs en competities', words: 'voetbal clubs competities' },
  { id: 'look', group: 'uiterlijk', label: 'Thema en menu', hint: 'Skin, menu, Netflix-look', words: 'skin thema kleur menu netflix screensaver startscherm widgets titelpagina beoordelingen ouderinfo recensies reviews' },
  { id: 'artwork', group: 'uiterlijk', label: 'Badges, metadata en artwork', hint: 'Badge Studio, posters, logo’s', words: 'badges badge studio dolby vision atmos 4k fanart tmdb spatialposters posters logo taal labels metadata' },
  { id: 'addons', group: 'bronnen', label: 'Addons', hint: 'Video- en muziekbronnen', words: 'stremio torrentio' },
  { id: 'debrid', group: 'bronnen', label: 'Debrid', hint: 'TorBox, Real-Debrid, AllDebrid…', words: 'torbox real-debrid realdebrid alldebrid premiumize debrid sleutel api key torrentio' },
  { id: 'connections', group: 'bronnen', label: 'Koppelingen', hint: 'Trakt, SIMKL, Seerr, Last.fm', words: 'tmdb trakt simkl mdblist seerr lastfm sleutels api' },
  { id: 'mystats', group: 'account', label: 'Mijn kijken', hint: 'Vandaag, deze maand, 13 weken', href: '/stats', words: 'statistieken kijktijd heatmap uren maand vandaag kijkpagina' },
  { id: 'taste', group: 'account', label: 'Mijn smaakprofiel', hint: 'Wat je graag kijkt', words: 'smaak genres gewoontes statistieken' },
  { id: 'household', group: 'account', label: 'Gezin en profielen', hint: 'Avatar, profielen, kinderslot', words: 'avatar profielfoto foto profielen kinderslot pin bedtijd samen kijken afstandsbediening wachtwoord' },
  { id: 'system', group: 'server', label: 'Systeem', hint: 'Tautulli, back-up, app', owner: true, words: 'tautulli back-up backup installeren app status' },
] as const satisfies readonly { id: string; group: Group; label: string; hint: string; owner?: boolean; href?: string; words: string }[];
/** Basis-modus: Start, Kijken (audio, afspelen), Uiterlijk en voor de beheerder het Overzicht. */
const BASIC = ['quick', 'mystats', 'overview', 'av', 'playback', 'look', 'debrid', 'household'] as const;

export type SettingsSection = (typeof SETTINGS_SECTIONS)[number]['id'];
type Section = (typeof SETTINGS_SECTIONS)[number];

export function isSettingsSection(x: string | undefined): x is SettingsSection {
  return SETTINGS_SECTIONS.some((s) => s.id === x);
}

const hrefOf = (s: Section) => ('href' in s ? s.href : `/settings/${s.id}`);

/** Instellingen, ingedeeld in groepen (links een menu met zoeken, rechts de instellingen). */
export function Settings({ section }: { section?: SettingsSection }) {
  const owner = useIsOwner();
  const [query, setQuery] = useState('');
  // Basis: alleen de belangrijkste onderdelen (zoals DexHub); "Alle instellingen" toont de rest.
  // Standaard basis voor gewone profielen, alles voor de beheerder; deze browser onthoudt de keuze.
  const [basic, setBasicState] = useState<boolean | null>(() => {
    try {
      const v = localStorage.getItem('tumedia.settingsBasic');
      return v === null ? null : v === '1';
    } catch {
      return null;
    }
  });
  const isBasic = basic ?? owner === false;
  const setBasic = (v: boolean) => {
    setBasicState(v);
    try { localStorage.setItem('tumedia.settingsBasic', v ? '1' : '0'); } catch { /* geen opslag */ }
  };
  const allVisible = SETTINGS_SECTIONS.filter((s) => !('owner' in s && s.owner) || owner);
  const visible = isBasic ? allVisible.filter((s) => (BASIC as readonly string[]).includes(s.id)) : allVisible;
  // Zonder gekozen onderdeel: de beheerder begint bij het Overzicht, anderen bij Snel instellen
  const fallback = owner ? 'overview' : 'quick';
  const wanted = section ?? (owner === null ? null : fallback);
  const own = allVisible.filter((s) => !('href' in s));
  const current = !wanted ? null : own.find((s) => s.id === wanted) ?? (owner === null ? null : own.find((s) => s.id === 'av') ?? null);
  const q = query.trim().toLowerCase();
  const matches = q ? allVisible.filter((s) => `${s.label} ${s.hint} ${s.words}`.toLowerCase().includes(q)) : null;

  const navItem = (s: Section) => (
    <a key={s.id} className="settings-nav-item" {...linkProps(hrefOf(s))} aria-current={current && s.id === current.id ? 'page' : undefined}>
      <span className="settings-nav-label">{s.label}{'href' in s ? <span className="settings-nav-ext" aria-hidden="true"> ›</span> : null}</span>
      <span className="settings-nav-hint">{s.hint}</span>
    </a>
  );

  return (
    <main className="page settings settings-split">
      <header className="page-head">
        <h1>Instellingen</h1>
      </header>
      <div className="settings-layout">
        <nav className="settings-nav" aria-label="Onderdelen van instellingen" data-focus-group>
          <form
            className="settings-search"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              if (matches?.[0]) {
                navigate(hrefOf(matches[0]));
                setQuery('');
              }
            }}
          >
            <label className="visually-hidden" htmlFor="settings-q">Zoek een instelling</label>
            <input id="settings-q" type="search" className="text-input" placeholder={owner ? 'Zoek of spring naar…' : 'Zoek een instelling…'} value={query} onChange={(e) => setQuery(e.target.value)} autoComplete="off" />
          </form>
          {matches ? (
            <div className="settings-nav-group">
              <span className="settings-nav-group-label">{matches.length ? `${matches.length} gevonden` : 'Niets gevonden'}</span>
              {matches.map(navItem)}
            </div>
          ) : (
            <>
              {GROUPS.filter((g) => (!g.owner || owner) && visible.some((s) => s.group === g.id)).map((g) => (
                <div key={g.id} className="settings-nav-group" role="group" aria-label={g.label}>
                  <span className="settings-nav-group-label" aria-hidden="true">{g.label}</span>
                  {visible.filter((s) => s.group === g.id).map(navItem)}
                </div>
              ))}
              <button className="btn btn-ghost btn-sm settings-basic-toggle" onClick={() => setBasic(!isBasic)}>
                {isBasic ? `Alle instellingen (${allVisible.length})` : 'Alleen de basis'}
              </button>
            </>
          )}
        </nav>
        <div className="settings-content">
          {!current && <p className="placeholder-row" aria-busy="true">Laden…</p>}
          {current && <h2 className="settings-section-title">{current.label}</h2>}
          {current?.id === 'quick' && <QuickSetup />}
          {current?.id === 'overview' && <AdminOverview />}
          {current?.id === 'users' && <UsersAdmin />}
          {current?.id === 'invites' && <InviteSettings />}
          {current?.id === 'av' && <PlaybackSettings title="Audio en ondertitels" groups={['audio', 'subs', 'substyle']} />}
          {current?.id === 'playback' && (
            <>
              <PlaybackSettings title="Afspelen" groups={['playback']} />
              <SourceOrderSetting />
              <SmartSourceSettings />
            </>
          )}
          {current?.id === 'look' && (
            <>
              <ThemePicker />
              <NavSettings />
              <Connections parts={['home']} />
              <PlaybackSettings title="Titelpagina" groups={['titlepage']} intro={false} />
              <PlaybackSettings title="Netflix-look" groups={['netflix']} intro={false} />
              <PlaybackSettings title="Screensaver" groups={['screensaver']} intro={false} />
            </>
          )}
          {current?.id === 'artwork' && (
            <>
              <BadgeStudio />
              <PosterLabelSettings />
              <Connections parts={['artwork']} />
            </>
          )}
          {current?.id === 'iptv' && <IptvSettings />}
          {current?.id === 'addons' && <AddonSettings />}
          {current?.id === 'debrid' && <DebridSettings />}
          {current?.id === 'taste' && <MyTaste />}
          {current?.id === 'sport' && <SportCenterSettings inSettings />}
          {current?.id === 'connections' && (
            <>
              <Connections parts={['keys', 'trakt']} />
              <SyncSettings />
              <SeerrSettings />
              <MovieProfileSettings />
              <UsenetSettings />
              <LastfmSettings />
            </>
          )}
          {current?.id === 'household' && (
            <>
              <AvatarSettings />
              <LocalAccountSettings />
              <AdminLink />
              <ParentalSettings />
              <section className="settings-block" aria-label="Samen kijken en afstandsbediening">
                <h2>Samen kijken en afstandsbediening</h2>
                <p className="subtle hint">Tegelijk kijken op verschillende apparaten, of je telefoon gebruiken als afstandsbediening voor dit scherm.</p>
                <div className="form-actions">
                  <a className="btn" {...linkProps('/together')}>Samen kijken</a>
                  <button className="btn" onClick={openRemotePairing}>Telefoon als afstandsbediening</button>
                  <a className="btn" {...linkProps('/remote')}>Dit is de afstandsbediening</a>
                </div>
              </section>
            </>
          )}
          {current?.id === 'system' && (
            <>
              <section className="settings-block" aria-label="Overzicht">
                <h2>Status</h2>
                <div className="form-actions">
                  <a className="btn" {...linkProps('/settings/overview')}>Overzicht</a>
                  <a className="btn" {...linkProps('/settings/status')}>Statusdetails</a>
                  <a className="btn" {...linkProps('/stats')}>Jouw kijkoverzicht</a>
                </div>
              </section>
              <TautulliSettings />
              <InstallApp />
              <BackupSettings />
            </>
          )}
          <div className="footer-space" />
        </div>
      </div>
    </main>
  );
}

/** Alleen de beheerder: naar Gebruikers & rechten. Anderen zien wat hun profiel mag. */
function AdminLink() {
  const { me } = useApp();
  const policy = usePolicy(me.profile?.uuid ?? '');
  const [owner, setOwner] = useState<boolean | null>(null);
  useEffect(() => {
    seerrApi.status().then((s) => setOwner(s.canManage)).catch(() => setOwner(false));
  }, []);
  if (owner) {
    return (
      <section className="settings-block" aria-label="Gebruikers en rechten">
        <h2>Gebruikers &amp; rechten</h2>
        <p className="subtle hint">Iedereen die tumedia gebruikt: je eigen profielen en vrienden met een eigen Plex-account. Per profiel wat mag (Live TV, aanvragen, addons, muziek, kwaliteit, skin) en aanvragen goedkeuren.</p>
        <div className="form-actions"><a className="btn btn-primary" {...linkProps('/settings/profiles')}>Gebruikers beheren</a></div>
      </section>
    );
  }
  if (!policy) return null;
  const rows: [string, boolean][] = [['Live TV', policy.liveTv], ['Aanvragen', policy.requests], ['Addons', policy.addons], ['Muziek', policy.music], ['Startscherm aanpassen', policy.layoutEdit]];
  return (
    <section className="settings-block" aria-label="Wat dit profiel mag">
      <h2>Wat dit profiel mag</h2>
      <p className="subtle hint">Ingesteld door de beheerder van de server.{policy.requests && policy.requestApproval ? ' Aanvragen worden eerst goedgekeurd.' : ''}</p>
      {rows.map(([k, v]) => <div key={k} className="meta-line"><span className="subtle">{k}</span><span>{v ? 'Aan' : 'Uit'}</span></div>)}
    </section>
  );
}

/** IPTV-accounts horen bij het Plex-account; per profiel aan/uit. Kinderprofielen staan standaard uit. */
function IptvSettings() {
  const { me } = useApp();
  const [accounts, setAccounts] = useState<IptvAccount[] | null>(null);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = () => iptv.accounts().then((r) => setAccounts(r.accounts)).catch(() => setError('Accounts laden mislukt.'));

  useEffect(() => {
    void load();
    api.profiles().then((r) => setProfiles(r.profiles)).catch(() => setProfiles([]));
  }, []);

  useEffect(() => {
    if (accounts && accounts.length === 0) setAdding(true);
  }, [accounts]);

  return (
    <>
      <p className="muted settings-intro">
        IPTV-accounts horen bij het Plex-account <strong>{me.account.username}</strong>. Elk profiel van dit account kan ze gebruiken,
        behalve als je dat hieronder uitzet. Wachtwoorden worden versleuteld op je eigen server bewaard en zijn hierna niet meer zichtbaar.
      </p>
      {error && <p className="placeholder-row error" role="alert">{error}</p>}
      {accounts && accounts.length > 1 && (
        <p className="subtle hint settings-intro">
          <strong>Reservezenders staan aan.</strong> Valt een zender weg, of zit een account aan zijn maximum aantal verbindingen,
          dan speelt tumedia dezelfde zender vanzelf via je andere account (zelfde naam en land). Favorieten en gids blijven gewoon gelijk.
        </p>
      )}
      {accounts?.map((a) => <AccountCard key={a.id} account={a} profiles={profiles} onChanged={load} />)}
      {adding ? (
        <AddAccount onDone={() => { setAdding(false); void load(); }} onCancel={accounts?.length ? () => setAdding(false) : undefined} />
      ) : (
        <div className="settings-block"><button className="btn" onClick={() => setAdding(true)}>+ IPTV-account toevoegen</button></div>
      )}
    </>
  );
}

/** Beheerder: uit welke Radarr-profielen kijkers bij een film mogen kiezen, en welk profiel bij animatie/familie wordt voorgesteld. */
function MovieProfileSettings() {
  type Srv = { id: number; name: string; isDefault: boolean; is4k: boolean; defaultProfileId: number; profiles: { id: number; name: string }[] };
  const [servers, setServers] = useState<Srv[] | null>(null);
  const [serverId, setServerId] = useState<number | null>(null);
  const [ids, setIds] = useState<number[]>([]);
  const [kidsId, setKidsId] = useState<number | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    seerrApi.radarr().then((r) => {
      setServers(r.servers);
      const sv = r.config?.serverId ?? r.servers.find((x) => x.isDefault && !x.is4k)?.id ?? r.servers[0]?.id ?? null;
      setServerId(sv);
      setIds(r.config?.ids ?? []);
      setKidsId(r.config?.kidsId ?? null);
    }).catch(() => setServers([]));
  }, []);
  if (!servers?.length) return null;
  const srv = servers.find((x) => x.id === serverId) ?? servers[0];
  const toggle = (id: number) => setIds((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const save = async () => {
    setMsg(null);
    const r = await seerrApi.saveMovieProfiles({ serverId: srv.id, ids, kidsId }).catch(() => null);
    setMsg(r ? (r.config ? 'Opgeslagen. Bij het aanvragen van een film kies je nu uit deze profielen.' : 'Uitgezet: films gaan weer met het standaardprofiel.') : 'Opslaan mislukt.');
  };
  return (
    <div className="settings-block form-grid">
      <h2>Kwaliteit kiezen bij films</h2>
      <p className="subtle hint">Kies minstens twee Radarr-profielen; kijkers kiezen daaruit bij het aanvragen van een film. Series gaan altijd met het standaardprofiel van Sonarr.</p>
      {servers.length > 1 && (
        <label>Radarr-server
          <select value={srv.id} onChange={(e) => { setServerId(Number(e.target.value)); setIds([]); setKidsId(null); }}>
            {servers.map((x) => <option key={x.id} value={x.id}>{x.name}{x.is4k ? ' (4K)' : ''}</option>)}
          </select>
        </label>
      )}
      <div className="season-list">
        {srv.profiles.map((p) => (
          <label key={p.id} className="toggle">
            <input type="checkbox" checked={ids.includes(p.id)} onChange={() => toggle(p.id)} />
            <span>{p.name}{p.id === srv.defaultProfileId ? <span className="subtle"> · standaard in Seerr</span> : null}</span>
          </label>
        ))}
      </div>
      {ids.length > 1 && (
        <label>Voorstellen bij animatie en familiefilms
          <select value={kidsId ?? ''} onChange={(e) => setKidsId(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Geen voorstel (altijd het standaardprofiel)</option>
            {srv.profiles.filter((p) => ids.includes(p.id)).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      )}
      {msg && <p className="subtle" role="status">{msg}</p>}
      <div className="form-actions"><button className="btn btn-primary" onClick={() => void save()}>Opslaan</button></div>
    </div>
  );
}

function SeerrSettings() {
  const [st, setSt] = useState<{ configured: boolean; url: string | null; canManage: boolean } | null>(null);
  const [url, setUrl] = useState('');
  const [key, setKey] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    seerrApi.status().then((s) => { setSt(s); setUrl(s.url ?? ''); }).catch(() => setSt(null));
  }, []);

  if (!st?.canManage) {
    return st?.configured ? null : null;
  }

  return (
    <form
      className="settings-block form-grid"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setMsg(null);
        try {
          const r = await seerrApi.saveConfig(url, key);
          setSt({ ...st, ...r });
          setKey('');
          setMsg('Gekoppeld.');
        } catch (err) {
          setMsg(err instanceof ApiError && err.message !== err.code ? err.message : 'Koppelen mislukt.');
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="account-head">
        <h2>Seerr (verzoeken)</h2>
        <span className={`status-pill ${st.configured ? 'ok' : 'warn'}`}>{st.configured ? 'Gekoppeld' : 'Niet gekoppeld'}</span>
      </div>
      <p className="subtle hint">
        Alleen jij als eigenaar van de Plex-server ziet dit. De API-sleutel vind je in Seerr onder Instellingen → Algemeen.
        Verzoeken komen op naam van de gebruiker als die in Seerr bekend is.
      </p>
      <label>Seerr-adres<input className="text-input" required value={url} onChange={(e) => setUrl(e.target.value)} placeholder="http://192.168.1.3:5055" inputMode="url" /></label>
      <label>API-sleutel<input className="text-input" required={!st.configured} type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder={st.configured ? 'Opgeslagen — alleen invullen om te wijzigen' : ''} autoComplete="off" /></label>
      {msg && <p className="subtle" role="status">{msg}</p>}
      <div className="form-actions">
        <button className="btn btn-primary" type="submit" disabled={busy || !key}>{busy ? 'Testen…' : st.configured ? 'Opnieuw koppelen' : 'Koppelen'}</button>
        {st.configured && (
          <button className="btn btn-ghost" type="button" onClick={async () => { await seerrApi.clearConfig().catch(() => undefined); setSt({ ...st, configured: false }); }}>
            Ontkoppelen
          </button>
        )}
      </div>
    </form>
  );
}

export function AddAccount({ onDone, onCancel }: { onDone: () => void; onCancel?: () => void }) {
  const [kind, setKind] = useState<'xtream' | 'm3u' | 'stalker'>('xtream');
  const [form, setForm] = useState({ name: '', serverUrl: '', username: '', password: '', max: '', url: '', portal: '', mac: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const max = form.max ? Number(form.max) : undefined;
      if (kind === 'm3u') await iptv.addOther({ kind, name: form.name || undefined, url: form.url, maxConnectionsOverride: max });
      else if (kind === 'stalker') await iptv.addOther({ kind, name: form.name || undefined, portalUrl: form.portal, mac: form.mac, maxConnectionsOverride: max });
      else await iptv.addAccount({
        name: form.name || undefined,
        serverUrl: form.serverUrl,
        username: form.username,
        password: form.password,
        maxConnectionsOverride: max,
      });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError && err.message !== err.code ? err.message : 'Toevoegen mislukt. Controleer de gegevens.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="settings-block form-grid" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <h2>IPTV-account toevoegen</h2>
      <div className="qs-chips" role="radiogroup" aria-label="Soort account">
        {([['xtream', 'Xtream-login'], ['m3u', 'M3U-lijst'], ['stalker', 'Stalker-portal (MAC)']] as const).map(([k, l]) => (
          <button key={k} type="button" role="radio" className="chip" aria-checked={kind === k} aria-pressed={kind === k} onClick={() => setKind(k)}>{l}</button>
        ))}
      </div>
      <p className="subtle hint">
        {kind === 'xtream' && 'Server, gebruikersnaam en wachtwoord van je aanbieder. Films en series van de aanbieder verschijnen ook als bron bij een titel.'}
        {kind === 'm3u' && 'De link naar je afspeellijst (.m3u of .m3u8). Staat er een gids in de lijst, dan gebruiken we die; anders kun je hierna een EPG-link invullen.'}
        {kind === 'stalker' && 'Het adres van het portal (vaak eindigend op /c/) en het MAC-adres dat je bij je aanbieder hebt opgegeven. Een gids vul je hierna in als EPG-link.'}
      </p>
      <label>Naam (optioneel)<input className="text-input" value={form.name} onChange={set('name')} placeholder="bijv. Mijn provider" /></label>
      {kind === 'xtream' && (
        <>
          <label>Server-URL<input className="text-input" required value={form.serverUrl} onChange={set('serverUrl')} placeholder="http://provider.example:8080" inputMode="url" autoComplete="off" /></label>
          <label>Gebruikersnaam<input className="text-input" required value={form.username} onChange={set('username')} autoComplete="off" /></label>
          <label>Wachtwoord<input className="text-input" required type="password" value={form.password} onChange={set('password')} autoComplete="new-password" /></label>
        </>
      )}
      {kind === 'm3u' && (
        <label>Link naar de lijst<input className="text-input" required type="password" value={form.url} onChange={set('url')} placeholder="http://…/playlist.m3u" autoComplete="off" /></label>
      )}
      {kind === 'stalker' && (
        <>
          <label>Portal<input className="text-input" required value={form.portal} onChange={set('portal')} placeholder="http://portal.example:8080/c/" inputMode="url" autoComplete="off" /></label>
          <label>MAC-adres<input className="text-input" required value={form.mac} onChange={set('mac')} placeholder="00:1A:79:12:34:56" autoComplete="off" spellCheck={false} /></label>
        </>
      )}
      <label>
        Max. gelijktijdige streams (optioneel)
        <input className="text-input" type="number" min={1} max={20} value={form.max} onChange={set('max')} placeholder="leeg = wat de provider toestaat" />
      </label>
      {error && <p className="error" role="alert">{error}</p>}
      <div className="form-actions">
        <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Controleren bij provider…' : 'Toevoegen'}</button>
        {onCancel && <button className="btn" type="button" onClick={onCancel}>Annuleren</button>}
      </div>
    </form>
  );
}

function AccountCard({ account: a, profiles, onChanged }: { account: IptvAccount; profiles: Profile[]; onChanged: () => void }) {
  const [max, setMax] = useState(a.maxConnectionsOverride ? String(a.maxConnectionsOverride) : '');
  const [epgUrl, setEpgUrl] = useState(a.epgUrl ?? '');
  const [epgMsg, setEpgMsg] = useState<string | null>(null);
  const [showChannels, setShowChannels] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);

  const patch = async (b: Parameters<typeof iptv.updateAccount>[1]) => {
    setBusy(true);
    await iptv.updateAccount(a.id, b).catch(() => undefined);
    setBusy(false);
    onChanged();
  };

  const enabledFor = (p: Profile) => !a.disabledProfiles.includes(p.uuid) && (!p.restricted || a.enabledProfiles.includes(p.uuid));
  const toggleProfile = (p: Profile) => {
    const on = !enabledFor(p);
    const disabled = new Set(a.disabledProfiles);
    const enabled = new Set(a.enabledProfiles);
    if (on) {
      disabled.delete(p.uuid);
      if (p.restricted) enabled.add(p.uuid);
    } else {
      disabled.add(p.uuid);
      enabled.delete(p.uuid);
    }
    void patch({ disabledProfiles: [...disabled], enabledProfiles: [...enabled] });
  };

  const expired = a.expiresAt !== null && a.expiresAt < Date.now();

  return (
    <section className="settings-block" aria-label={a.name}>
      <div className="account-head">
        <h2>{a.name}</h2>
        <span className={`status-pill ${a.status === 'Active' && !expired ? 'ok' : 'warn'}`}>{expired ? 'Verlopen' : a.status ?? 'Onbekend'}</span>
      </div>
      <div className="meta-line"><span className="subtle">{a.kind === 'm3u' ? 'Lijst van' : a.kind === 'stalker' ? 'Portal' : 'Server'}</span><span>{a.serverUrl}</span></div>
      {(a.kind ?? 'xtream') === 'xtream' ? <div className="meta-line"><span className="subtle">Gebruiker</span><span>{a.username}</span></div>
        : <div className="meta-line"><span className="subtle">Soort</span><span>{a.kind === 'm3u' ? 'M3U-lijst' : 'Stalker-portal (MAC)'}</span></div>}
      {a.expiresAt && <div className="meta-line"><span className="subtle">Geldig tot</span><span>{new Date(a.expiresAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}</span></div>}
      <div className="meta-line"><span className="subtle">Streams</span><span>max. {a.maxConnections} tegelijk (provider staat {a.providerMaxConnections} toe)</span></div>

      <form className="inline-form" onSubmit={(e) => { e.preventDefault(); void patch({ maxConnectionsOverride: max ? Number(max) : null }); }}>
        <label htmlFor={`max-${a.id}`}>Eigen limiet</label>
        <input id={`max-${a.id}`} className="text-input text-input-sm" type="number" min={1} max={a.providerMaxConnections} value={max} onChange={(e) => setMax(e.target.value)} placeholder={String(a.providerMaxConnections)} />
        <button className="btn" type="submit" disabled={busy}>Opslaan</button>
      </form>

      <form
        className="epg-form"
        onSubmit={async (e) => {
          e.preventDefault();
          setEpgMsg(null);
          try {
            await iptv.updateAccount(a.id, { epgUrl: epgUrl.trim() || null });
            setEpgMsg(epgUrl.trim() ? 'Opgeslagen. De gids wordt opnieuw opgehaald; dat kan even duren.' : 'Externe gids verwijderd.');
            onChanged();
          } catch (err) {
            setEpgMsg(err instanceof ApiError && err.message !== err.code ? err.message : 'Opslaan mislukt.');
          }
        }}
      >
        <label htmlFor={`epg-${a.id}`}>Externe EPG (XMLTV, optioneel)</label>
        <p className="subtle hint">
          Een link naar een XMLTV-gids, ook <code>.xml.gz</code>. Zenders worden gekoppeld op EPG-id of anders op naam;
          waar de externe gids niets heeft, blijft de gids van je provider staan.
        </p>
        <div className="inline-form">
          <input id={`epg-${a.id}`} className="text-input" type="url" inputMode="url" value={epgUrl} onChange={(e) => setEpgUrl(e.target.value)} placeholder="https://…/epg.xml.gz" />
          <button className="btn" type="submit">Opslaan</button>
        </div>
        {epgMsg && <p className="subtle" role="status">{epgMsg}</p>}
      </form>

      <div className="settings-sub">
        <div className="account-head">
          <h3>Bouquets en zenders</h3>
          <span className="subtle">{a.hiddenCategories.length ? `${a.hiddenCategories.length} bouquets uit` : 'alles zichtbaar'}{a.hiddenStreams.length ? ` · ${a.hiddenStreams.length} zenders uit` : ''}</span>
          <button className="chip" aria-expanded={showChannels} onClick={() => setShowChannels((v) => !v)}>{showChannels ? 'Sluiten' : 'Beheren'}</button>
        </div>
        {showChannels && <ChannelManager accountId={a.id} onSaved={onChanged} />}
      </div>

      {profiles.length > 0 && (
        <fieldset className="profile-toggles">
          <legend>Gebruiken door profiel</legend>
          {profiles.map((p) => (
            <label key={p.uuid} className="toggle">
              <input type="checkbox" checked={enabledFor(p)} onChange={() => toggleProfile(p)} disabled={busy} />
              <span>{p.title}{p.restricted ? ' (kind)' : ''}</span>
            </label>
          ))}
        </fieldset>
      )}

      <div className="form-actions">
        {confirmDelete ? (
          <>
            <span className="error">Account en favorieten verwijderen?</span>
            <button className="btn btn-danger" onClick={async () => { await iptv.deleteAccount(a.id).catch(() => undefined); onChanged(); }}>Ja, verwijderen</button>
            <button className="btn" onClick={() => setConfirmDelete(false)}>Annuleren</button>
          </>
        ) : (
          <button className="btn btn-ghost" onClick={() => setConfirmDelete(true)}>Account verwijderen</button>
        )}
      </div>
    </section>
  );
}
